import React, { useEffect } from "react";
import { useDispatch } from "react-redux";

import { setIsMidstreamNavigatorFilter as setIsMidstreamNavigatorFacilityFilter } from "store/features/filter/facilityFilterSlice";
import { setIsMidstreamNavigatorFilter } from "store/features/filter/filterSlice";
import { useMapStore } from "stores/map";
import {
  selectHighlightContributingWellsForFacility,
  selectSelectedFacilities
} from "stores/map/mapSelectors";

import { useUserEvents } from "hooks";

import Icon_MidstreamHighlight from "components/icons/Icon_MidstreamHighlight";
import {
  midstreamNavigatorActions,
  useMidstreamNavigatorContext
} from "components/midstream/midstream-navigator/context/MidstreamNavigatorContext";

import { FilterKey, useFilterButtonContext } from "../context/FilterButtonContext";
import { BaseMidstreamFilterButton, FilterConfig } from "../filters/BaseFilterButton";

export const HighlightWellsButton: React.FC = () => {
  const [state, dispatch] = useMidstreamNavigatorContext();
  const reduxDispatch = useDispatch();
  const { getIsDisabled, withUserAction } = useFilterButtonContext();
  const { sendMidstreamNavigatorButtonToggledEvent } = useUserEvents();

  const selectedFacilities = useMapStore(selectSelectedFacilities);

  // Monitor Zustand highlight state changes to sync with local highlight state
  const highlightContributingWellsForFacility = useMapStore(
    selectHighlightContributingWellsForFacility
  );

  const setHighlightContributingWellsForFacility = useMapStore(
    (s) => s.setHighlightContributingWellsForFacility
  );
  const clearHighlightContributingWellsForFacility = useMapStore(
    (s) => s.clearHighlightContributingWellsForFacility
  );

  const config: FilterConfig = {
    icon: <Icon_MidstreamHighlight />,
    tooltip: "Highlight Contributing Wells for Selected Facility",
    testId: `filter-button-${FilterKey.HighlightWells}`
  };

  const isActive = state.highlightActive ?? false;
  const isDisabled = getIsDisabled(FilterKey.HighlightWells, isActive);
  const disabledReason = isDisabled ? "Select a facility to highlight" : null;

  useEffect(() => {
    if (highlightContributingWellsForFacility === null && state.highlightActive) {
      // Zustand state was cleared, deactivate local highlight
      dispatch(midstreamNavigatorActions.toggleHighlight(false));
    }
  }, [highlightContributingWellsForFacility, state.highlightActive, dispatch]);

  // Auto-cleanup when button becomes disabled while active
  useEffect(() => {
    if (isDisabled && isActive) {
      // Button became disabled while active - clean up the highlight
      dispatch(midstreamNavigatorActions.toggleHighlight(false));
      clearHighlightContributingWellsForFacility();
    }
  }, [isDisabled, isActive, dispatch, clearHighlightContributingWellsForFacility]);

  const handleToggle = () => {
    withUserAction(() => {
      if (!isActive) {
        const hasSelectedFacilities =
          selectedFacilities && Object.keys(selectedFacilities).length > 0;
        if (hasSelectedFacilities) {
          dispatch(midstreamNavigatorActions.toggleHighlight(true));
          reduxDispatch(setIsMidstreamNavigatorFilter(true));
          reduxDispatch(setIsMidstreamNavigatorFacilityFilter(true));
          const facilityId = Object.keys(selectedFacilities)[0];
          setHighlightContributingWellsForFacility(facilityId);
          sendMidstreamNavigatorButtonToggledEvent({
            filterKey: FilterKey.HighlightWells,
            selectedFacility: facilityId
          });
        }
      } else {
        dispatch(midstreamNavigatorActions.toggleHighlight(false));
        clearHighlightContributingWellsForFacility();
      }
    });
  };

  return (
    <BaseMidstreamFilterButton
      config={config}
      isActive={isActive}
      isDisabled={isDisabled}
      disabledReason={disabledReason}
      onToggle={handleToggle}
    />
  );
};
